import { useCallback, useEffect, useRef } from "react";

type UseHoverWithDelayOptions = {
  onOpen: () => void;
  onClose: () => void;
  shouldDisableOpen?: boolean;
  openDelay?: number;
  closeDelay?: number;
};

const useMainNavigationHoverWithDelay = ({
  onOpen,
  onClose,
  shouldDisableOpen = false,
  openDelay = 150,
  closeDelay = 200,
}: UseHoverWithDelayOptions) => {
  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const clearPendingTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = undefined;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (shouldDisableOpen) {
      return;
    }

    clearPendingTimeout();

    hoverTimeoutRef.current = setTimeout(() => {
      onOpen();
    }, openDelay);
  }, [onOpen, shouldDisableOpen, openDelay, clearPendingTimeout]);

  const handleMouseLeave = useCallback(() => {
    clearPendingTimeout();

    hoverTimeoutRef.current = setTimeout(() => {
      onClose();
    }, closeDelay);
  }, [onClose, closeDelay, clearPendingTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPendingTimeout();
    };
  }, [clearPendingTimeout]);

  return {
    handleMouseEnter,
    handleMouseLeave,
  };
};

export default useMainNavigationHoverWithDelay;
